<template>
  <KakaoChannelInvite 
    ref="kakaoInvite"
    badge="🎁"
    title="선물 수령 완료!"
    message="채널 추가하면<br/><strong>더 많은 혜택과 이벤트</strong> 소식을 받을 수 있어요!"
  />

  <div class="gift-claim-page">
    <div class="container">
      <!-- 상단 브랜딩 헤더 -->
      <div class="brand-header">
        <div class="brand-logo">
          <svg width="28" height="28" viewBox="0 0 40 40" fill="none">
            <rect width="40" height="40" rx="10" fill="#2D5BFF"/>
            <path d="M12 14h16v3H12zM12 20h12v3H12zM12 26h8v3H12z" fill="#fff"/>
          </svg>
          <span class="brand-name">보다</span>
        </div>
        <span class="brand-sub">보험 다이어리</span>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>선물 정보를 불러오는 중...</p>
      </div>

      <!-- 에러 -->
      <div v-else-if="error" class="error-state">
        <div class="state-icon error-icon">
          <i class="ph-fill ph-x-circle"></i>
        </div>
        <h2>{{ error }}</h2>
        <p>링크를 다시 확인해주세요</p>
      </div>

      <!-- 이미 수령 완료 -->
      <div v-else-if="giftData.status === 'claimed'" class="claimed-state">
        <div class="state-icon success-icon">
          <i class="ph-fill ph-check-circle"></i>
        </div>
        <h2>이미 수령한 선물입니다</h2>
        <p>{{ formatDate(giftData.claimed_at) }} 수령 완료</p>
      </div>

      <!-- ========== 수령 대기 ========== -->
      <div v-else class="claim-form">

        <!-- ===== PHASE 1: 선물 상자 (미개봉) ===== -->
        <div v-if="!revealed" class="unopened-phase">
          <!-- 보낸 사람 -->
          <div class="sender-info">
            <div class="sender-avatar">
              <i class="ph-fill ph-user"></i>
            </div>
            <div class="sender-detail">
              <p class="sender-label">보낸 사람</p>
              <p class="sender-name">{{ giftData.sender_name }} 설계사</p>
            </div>
          </div>

          <!-- 🎁 선물 상자 씬 -->
          <div class="gift-box-scene">
            <!-- 파티클 -->
            <div class="particles" :class="{ active: boxOpening }">
              <span v-for="i in 20" :key="i" class="particle"></span>
            </div>

            <!-- 선물 상자 -->
            <div class="gift-box" :class="{ opening: boxOpening }">
              <div class="box-lid">
                <div class="lid-face"></div>
                <div class="ribbon-bow">
                  <div class="bow-loop bow-left"></div>
                  <div class="bow-loop bow-right"></div>
                  <div class="bow-knot"></div>
                </div>
              </div>
              <div class="box-body">
                <div class="ribbon-v"></div>
                <div class="ribbon-h"></div>
                <div class="box-shine"></div>
              </div>
              <!-- 상자 안 물음표 -->
              <div class="box-mystery">
                <span>?</span>
              </div>
            </div>

            <p class="box-label" v-if="!boxOpening">선물이 도착했어요!</p>
            <p class="box-label opening-label" v-if="boxOpening">두근두근...</p>
          </div>

          <!-- 메시지 -->
          <div v-if="giftData.gift_message && !boxOpening" class="gift-message-section">
            <div class="gift-message-bubble">
              <i class="ph-fill ph-quotes"></i>
              <p>{{ giftData.gift_message }}</p>
            </div>
          </div>

          <!-- 수신 대상자 힌트 -->
          <div class="receiver-hint" v-if="maskedPhone">
            <i class="ph-fill ph-user-check"></i>
            <div>
              <p class="hint-main"><strong>{{ maskedPhone }}</strong> 님에게 보낸 선물</p>
              <p class="hint-sub">본인 확인 후 선물을 열어보세요</p>
            </div>
          </div>

          <!-- 본인 확인 -->
          <div class="verify-section">
            <div class="verify-header">
              <i class="ph-fill ph-shield-check"></i>
              <div>
                <h3>본인 확인</h3>
                <p class="verify-desc">전화번호를 입력하여 선물을 열어보세요</p>
              </div>
            </div>
            
            <div class="phone-input">
              <label>휴대폰 번호</label>
              <input 
                type="tel" 
                v-model="phone"
                placeholder="010-0000-0000"
                @input="formatPhone"
                maxlength="13"
              />
            </div>

            <button 
              class="claim-btn" 
              :disabled="!canClaim || claiming"
              @click="handleClaim"
            >
              <div class="spinner-sm" v-if="claiming"></div>
              <template v-else>
                <i class="ph-fill ph-gift"></i>
                <span>선물 열어보기</span>
              </template>
            </button>

            <div class="safety-note">
              <i class="ph-fill ph-lock"></i>
              <span>입력하신 정보는 본인 확인 용도로만 사용되며 별도 저장되지 않습니다</span>
            </div>
          </div>

          <!-- 의심되시나요 -->
          <div class="trust-section">
            <button class="trust-toggle" @click="showTrustInfo = !showTrustInfo">
              <i class="ph-fill ph-question"></i>
              <span>이 페이지가 의심되시나요?</span>
              <i class="ph-bold" :class="showTrustInfo ? 'ph-caret-up' : 'ph-caret-down'"></i>
            </button>
            <transition name="slide">
              <div class="trust-content" v-if="showTrustInfo">
                <div class="trust-item">
                  <i class="ph-fill ph-check-circle"></i>
                  <div>
                    <strong>발송 번호 확인</strong>
                    <p>본 메시지는 1644-7932(보다 공식 발신번호)에서 발송되었습니다</p>
                  </div>
                </div>
                <div class="trust-item">
                  <i class="ph-fill ph-check-circle"></i>
                  <div>
                    <strong>담당 설계사에게 확인</strong>
                    <p>선물을 보낸 <strong>{{ giftData.sender_name }}</strong> 설계사님에게 직접 연락하여 확인하실 수 있습니다</p>
                  </div>
                </div>
                <div class="trust-item">
                  <i class="ph-fill ph-check-circle"></i>
                  <div>
                    <strong>고객센터 문의</strong>
                    <p>보다 고객센터 <a href="tel:16447932">1644-7932</a>로 문의해주세요</p>
                  </div>
                </div>
                <div class="trust-item">
                  <i class="ph-fill ph-check-circle"></i>
                  <div>
                    <strong>안전한 페이지</strong>
                    <p>본 페이지는 개인정보를 수집하거나 앱 설치를 요구하지 않습니다</p>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- ===== PHASE 2: 선물 공개 (개봉 후) ===== -->
        <div v-if="revealed" class="revealed-phase">
          <!-- confetti 배경 -->
          <div class="confetti-bg">
            <span v-for="i in 30" :key="'c'+i" class="confetti" :style="confettiStyle(i)"></span>
          </div>

          <div class="reveal-header">
            <span class="tada">🎉</span>
            <h2>짜잔! 선물이 도착했어요</h2>
            <p>{{ giftData.sender_name }} 설계사님이 보낸 선물</p>
          </div>

          <!-- 상품 카드 -->
          <div class="product-card animate-in">
            <div class="product-image-wrap">
              <img 
                v-if="giftData.goods_img" 
                :src="giftData.goods_img" 
                :alt="giftData.goods_name"
                class="product-image"
              />
              <div v-else class="product-image-placeholder">
                <i class="ph-fill ph-gift"></i>
              </div>
            </div>
            <div class="product-detail">
              <span class="product-brand">{{ giftData.brand_name }}</span>
              <h3 class="product-name">{{ giftData.goods_name }}</h3>
            </div>
          </div>

          <!-- 메시지 -->
          <div v-if="giftData.gift_message" class="reveal-message">
            <i class="ph-fill ph-quotes"></i>
            <p>{{ giftData.gift_message }}</p>
          </div>

          <!-- 수령 완료 -->
          <div class="claim-success-box">
            <i class="ph-fill ph-check-circle"></i>
            <div>
              <strong>기프티콘이 발송되었습니다</strong>
              <p>{{ phone }} 번호로 기프티콘이 전송됩니다</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 하단 회사 정보 -->
      <div class="footer-info">
        <p class="footer-company">보다(BODA) · 보험 다이어리</p>
        <p class="footer-contact">고객센터 <a href="tel:16447932">1644-7932</a></p>
      </div>
    </div>

    <!-- 토스트 -->
    <transition name="toast">
      <div class="toast" v-if="showToastMsg" :class="toastType">
        <span class="toast-icon">{{ toastType === 'success' ? '✓' : '✕' }}</span>
        <span>{{ toastMessage }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted, inject } from 'vue';
import { useRoute } from 'vue-router';
import KakaoChannelInvite from '@/components/dialogs/KakaoChannelInvite.vue'


export default {
  name: 'GiftClaim',
  components: {
    KakaoChannelInvite
  },
  setup() {
    const route = useRoute();
    const token = route.params.token;
    const sql_query = inject('sql_query');
    const purchaseGifticon = inject('purchaseGifticon', null);

    const kakaoInvite = ref(null);
    kakaoInvite.value?.show()

    const loading = ref(true);
    const error = ref('');
    const giftData = ref({});
    const phone = ref('');
    const claiming = ref(false);
    const showTrustInfo = ref(false);

    // 선물 상자 애니메이션 상태
    const boxOpening = ref(false);
    const revealed = ref(false);

    // 토스트
    const showToastMsg = ref(false);
    const toastMessage = ref('');
    const toastType = ref('success');

    function showToast(msg, type = 'success') {
      toastMessage.value = msg;
      toastType.value = type;
      showToastMsg.value = true;
      setTimeout(() => showToastMsg.value = false, 3000);
    }

    const maskedPhone = computed(() => {
      const p = giftData.value.receiver_phone;
      if (!p || p.length < 10) return '';
      return p.slice(0, 3) + '-****-' + p.slice(-4);
    });

    // confetti 스타일
    function confettiStyle(i) {
      const colors = ['#FF6B6B', '#FFE66D', '#4ECDC4', '#45B7D1', '#FF9FF3', '#2D5BFF', '#FF9800', '#22C55E'];
      return {
        '--left': (Math.random() * 100) + '%',
        '--delay': (Math.random() * 3) + 's',
        '--duration': (2 + Math.random() * 3) + 's',
        '--color': colors[i % colors.length],
        '--size': (4 + Math.random() * 6) + 'px',
        '--drift': (Math.random() * 100 - 50) + 'px'
      };
    }

    async function fetchGiftData() {
      try {
        const sql = `
          SELECT 
            gl.*,
            gc.amount, gc.memo,
            m.name as sender_name
          FROM gift_links gl
          JOIN giftcoupon gc ON gl.coupon_idx = gc.idx
          LEFT JOIN member m ON gl.sender_id = m.mb_id
          WHERE gl.token = '${token}'
        `;
        const res = await sql_query(sql);

        if (res && res.error) {
          error.value = '선물 정보를 불러올 수 없습니다';
          loading.value = false;
          return;
        }
        if (!res || res.length === 0) {
          error.value = '선물을 찾을 수 없습니다';
          loading.value = false;
          return;
        }

        const link = res[0];
        if (new Date(link.expires_at) < new Date()) {
          error.value = '만료된 선물입니다';
          loading.value = false;
          return;
        }

        let memoData = {};
        try {
          memoData = typeof link.memo === 'string' ? JSON.parse(link.memo) : link.memo;
        } catch (e) { memoData = {}; }

        giftData.value = {
          status: link.status,
          sender_id: link.sender_id,
          sender_name: link.sender_name || link.sender_id,
          receiver_name: link.receiver_name,
          goods_code: link.goods_code || memoData.goods_code || '',
          goods_name: link.goods_name || memoData.goods_name || '',
          brand_name: link.brand_name || memoData.brand_name || '',
          brand_img: link.brand_img || memoData.brand_img || '',
          goods_img: link.goods_img || memoData.goods_img || '',
          sale_price: link.sale_price || memoData.sale_price || 0,
          discount_price: link.discount_price || memoData.discount_price || 0,
          gift_message: link.gift_message,
          claimed_at: link.claimed_at,
          coupon_idx: link.coupon_idx,
          receiver_phone: link.receiver_phone
        };
      } catch (e) {
        error.value = e.message || '선물 정보를 불러올 수 없습니다';
      } finally {
        loading.value = false;
      }
    }

    function formatPhone() {
      let numbers = phone.value.replace(/[^0-9]/g, '');
      if (numbers.length <= 3) {
        phone.value = numbers;
      } else if (numbers.length <= 7) {
        phone.value = numbers.slice(0, 3) + '-' + numbers.slice(3);
      } else {
        phone.value = numbers.slice(0, 3) + '-' + numbers.slice(3, 7) + '-' + numbers.slice(7, 11);
      }
    }

    const canClaim = computed(() => {
      const cleaned = phone.value.replace(/-/g, '');
      return cleaned.length === 11;
    });

    async function handleClaim() {
      if (!canClaim.value || claiming.value) return;
      const cleanedPhone = phone.value.replace(/-/g, '');

      if (giftData.value.receiver_phone !== cleanedPhone) {
        showToast('선물 수령 대상자가 아닙니다', 'error');
        return;
      }

      claiming.value = true;

      try {
        // Step 1: 상자 열기 애니메이션
        boxOpening.value = true;
        await new Promise(resolve => setTimeout(resolve, 1800));

        // Step 2: API 호출
        if (purchaseGifticon) {
          const result = await purchaseGifticon({
            mb_id: giftData.value.sender_id,
            goods_code: giftData.value.goods_code || '',
            goods_name: giftData.value.goods_name,
            brand_name: giftData.value.brand_name,
            goods_img: giftData.value.goods_img,
            sale_price: giftData.value.sale_price || 0,
            discount_price: giftData.value.discount_price || 0,
            recipient_type: 'customer',
            recipient_name: giftData.value.receiver_name || '',
            recipient_phone: cleanedPhone,
            customer_idx: null,
            mms_msg: giftData.value.gift_message || ''
          });
          if (!result.success) {
            boxOpening.value = false;
            showToast(result.message || '기프티콘 발송 실패', 'error');
            claiming.value = false;
            return;
          }
        }

        await sql_query(`
          UPDATE giftcoupon 
          SET status = '사용완료',
              issued_to = '${giftData.value.receiver_name}(${cleanedPhone})',
              ktgift_sent_at = NOW(),
              used_at = NOW()
          WHERE idx = ${giftData.value.coupon_idx}
        `);

        await sql_query(`
          UPDATE gift_links 
          SET status = 'claimed', claimed_at = NOW()
          WHERE token = '${token}'
        `);

        // customer_history 기록
        try {
          const custRes = await sql_query(`
            SELECT idx FROM customer 
            WHERE mb_id = '${giftData.value.sender_id}' 
            AND REPLACE(phone, '-', '') = '${cleanedPhone}'
            LIMIT 1
          `);
          if (custRes && custRes.length > 0) {
            const customerIdx = custRes[0].idx;
            const escapedName = (giftData.value.goods_name || '').replace(/'/g, "\\'");
            await sql_query(`
              INSERT INTO customer_history 
              (mb_id, customer_idx, type, title, content, status, created_at) 
              VALUES (
                '${giftData.value.sender_id}', 
                ${customerIdx}, 
                'service', 
                '선물 수령 완료', 
                '${escapedName} 기프티콘을 수령하였습니다.',
                'done',
                NOW()
              )
            `);
          }
        } catch (histErr) {
          console.error('customer_history 기록 실패:', histErr);
        }

        // Step 3: 공개 화면 전환
        await new Promise(resolve => setTimeout(resolve, 200));
        revealed.value = true;

      } catch (e) {
        boxOpening.value = false;
        showToast(e.message || '선물 수령 중 오류가 발생했습니다', 'error');
      } finally {
        claiming.value = false;
      }
    }

    function formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
    }

    onMounted(() => { fetchGiftData(); });

    return {
      loading, error, giftData, phone, claiming, canClaim,
      formatPhone, handleClaim, formatDate, maskedPhone,
      showToastMsg, toastMessage, toastType, showTrustInfo,
      boxOpening, revealed, confettiStyle,
      kakaoInvite
    };
  }
}
</script>

<style scoped>
/* ===== 전체 ===== */
.gift-claim-page {
  min-height: 100vh;
  background: #F0F2F5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif;
}
.container {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

/* ===== 브랜딩 헤더 ===== */
.brand-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}
.brand-logo { display: flex; align-items: center; gap: 10px; }
.brand-name { font-size: 18px; font-weight: 800; color: #2D5BFF; letter-spacing: -0.5px; }
.brand-sub { font-size: 12px; color: #999; font-weight: 500; }

/* ===== 상태 화면 ===== */
.loading, .error-state, .claimed-state { text-align: center; padding: 60px 20px; }
.spinner {
  width: 40px; height: 40px;
  border: 3px solid #e0e0e0; border-top-color: #2D5BFF;
  border-radius: 50%; animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}
.spinner-sm {
  width: 20px; height: 20px;
  border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
  border-radius: 50%; animation: spin 0.8s linear infinite;
  margin: 0 auto;
}
.loading p { color: #999; font-size: 14px; }
.state-icon {
  width: 64px; height: 64px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 16px;
}
.state-icon i { font-size: 36px; }
.error-icon { background: #FEE2E2; }
.error-icon i { color: #EF4444; }
.success-icon { background: #DCFCE7; }
.success-icon i { color: #22C55E; }
.error-state h2, .claimed-state h2 { margin: 0 0 8px; font-size: 18px; color: #333; font-weight: 700; }
.error-state p, .claimed-state p { margin: 0; font-size: 14px; color: #999; }

@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* ================================================================
   🎁 선물 상자 씬
   ================================================================ */
.gift-box-scene {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 20px 28px;
  background: linear-gradient(180deg, #FFF9F0 0%, #FFFFFF 100%);
  overflow: hidden;
  min-height: 260px;
}

.box-label {
  margin: 24px 0 0;
  font-size: 18px;
  font-weight: 800;
  color: #333;
  animation: fadeInUp 0.5s ease;
}
.opening-label {
  color: #2D5BFF;
  animation: pulse 0.6s ease infinite alternate;
}
@keyframes pulse {
  from { opacity: 0.6; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
}

/* --- 선물 상자 --- */
.gift-box {
  position: relative;
  width: 160px;
  height: 170px;
}

/* 상자 본체 */
.box-body {
  position: absolute;
  bottom: 0;
  left: 10px;
  right: 10px;
  height: 100px;
  background: linear-gradient(135deg, #FF6B6B 0%, #EE5A24 100%);
  border-radius: 6px 6px 10px 10px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(238, 90, 36, 0.25);
}
.ribbon-v {
  position: absolute; left: 50%; top: 0; bottom: 0;
  width: 18px; margin-left: -9px;
  background: linear-gradient(180deg, #FFE66D 0%, #F9CA24 100%);
}
.ribbon-h {
  position: absolute; top: 50%; left: 0; right: 0;
  height: 18px; margin-top: -9px;
  background: linear-gradient(90deg, #FFE66D 0%, #F9CA24 100%);
}
.box-shine {
  position: absolute; top: 0; left: 0; right: 0; height: 40%;
  background: linear-gradient(180deg, rgba(255,255,255,0.2) 0%, transparent 100%);
}

/* 물음표 */
.box-mystery {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  pointer-events: none;
}
.box-mystery span {
  font-size: 40px;
  font-weight: 900;
  color: rgba(255,255,255,0.4);
  text-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
.gift-box.opening .box-mystery {
  animation: mysteryFade 0.5s ease forwards;
}
@keyframes mysteryFade {
  to { opacity: 0; transform: translateX(-50%) translateY(-20px) scale(1.5); }
}

/* 상자 뚜껑 */
.box-lid {
  position: absolute;
  top: 22px;
  left: 4px;
  right: 4px;
  height: 48px;
  transform-origin: left bottom;
  transition: transform 1s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 3;
}
.lid-face {
  position: absolute;
  bottom: 0; left: 0; right: 0; height: 38px;
  background: linear-gradient(135deg, #FF8E7A 0%, #FF6B6B 100%);
  border-radius: 8px 8px 0 0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}

/* 리본 나비 */
.ribbon-bow {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 56px;
  height: 28px;
}
.bow-loop {
  position: absolute;
  top: 4px;
  width: 24px;
  height: 20px;
  background: linear-gradient(135deg, #FFE66D 0%, #F9CA24 100%);
}
.bow-left {
  left: 0;
  border-radius: 50% 50% 10% 50%;
  transform: rotate(-25deg);
}
.bow-right {
  right: 0;
  border-radius: 50% 50% 50% 10%;
  transform: rotate(25deg);
}
.bow-knot {
  position: absolute;
  top: 10px; left: 50%; transform: translateX(-50%);
  width: 12px; height: 12px;
  background: #F0B90B;
  border-radius: 50%;
  z-index: 1;
}

/* 🎁 상자 열기 애니메이션 */
.gift-box.opening .box-lid {
  animation: lidFlyOff 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
.gift-box.opening .box-body {
  animation: boxSquish 0.8s ease 0.2s;
}

@keyframes lidFlyOff {
  0%   { transform: rotateZ(0deg) translateY(0); }
  40%  { transform: rotateZ(-70deg) translateY(-50px); }
  100% { transform: rotateZ(-90deg) translateY(-80px) scale(0.7); opacity: 0; }
}
@keyframes boxSquish {
  0%   { transform: scaleY(1) scaleX(1); }
  25%  { transform: scaleY(0.85) scaleX(1.08); }
  55%  { transform: scaleY(1.06) scaleX(0.97); }
  100% { transform: scaleY(1) scaleX(1); }
}

/* --- 파티클 (상자 오픈 시 터지는 효과) --- */
.particles {
  position: absolute;
  top: 45%;
  left: 50%;
  z-index: 10;
  pointer-events: none;
}
.particle {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  opacity: 0;
}
/* 각 파티클 색상 */
.particle:nth-child(1)  { background: #FF6B6B; }
.particle:nth-child(2)  { background: #FFE66D; }
.particle:nth-child(3)  { background: #4ECDC4; }
.particle:nth-child(4)  { background: #45B7D1; }
.particle:nth-child(5)  { background: #FF9FF3; }
.particle:nth-child(6)  { background: #F368E0; }
.particle:nth-child(7)  { background: #2D5BFF; }
.particle:nth-child(8)  { background: #FF9800; }
.particle:nth-child(9)  { background: #22C55E; }
.particle:nth-child(10) { background: #EF4444; }
.particle:nth-child(11) { background: #FF6B6B; }
.particle:nth-child(12) { background: #FFE66D; }
.particle:nth-child(13) { background: #4ECDC4; }
.particle:nth-child(14) { background: #45B7D1; }
.particle:nth-child(15) { background: #FF9FF3; }
.particle:nth-child(16) { background: #F368E0; }
.particle:nth-child(17) { background: #2D5BFF; }
.particle:nth-child(18) { background: #FF9800; }
.particle:nth-child(19) { background: #22C55E; }
.particle:nth-child(20) { background: #EF4444; }

/* 파티클 각각 다른 방향으로 터짐 */
.particles.active .particle:nth-child(1)  { animation: pBurst 0.9s ease-out 0.0s forwards; --tx: 80px; --ty: -90px; }
.particles.active .particle:nth-child(2)  { animation: pBurst 0.9s ease-out 0.05s forwards; --tx: -70px; --ty: -100px; }
.particles.active .particle:nth-child(3)  { animation: pBurst 0.9s ease-out 0.02s forwards; --tx: 50px; --ty: -110px; }
.particles.active .particle:nth-child(4)  { animation: pBurst 0.9s ease-out 0.08s forwards; --tx: -100px; --ty: -60px; }
.particles.active .particle:nth-child(5)  { animation: pBurst 0.9s ease-out 0.03s forwards; --tx: 110px; --ty: -40px; }
.particles.active .particle:nth-child(6)  { animation: pBurst 0.9s ease-out 0.1s forwards; --tx: -50px; --ty: -120px; }
.particles.active .particle:nth-child(7)  { animation: pBurst 0.9s ease-out 0.04s forwards; --tx: 30px; --ty: -130px; }
.particles.active .particle:nth-child(8)  { animation: pBurst 0.9s ease-out 0.12s forwards; --tx: -110px; --ty: -80px; }
.particles.active .particle:nth-child(9)  { animation: pBurst 0.9s ease-out 0.06s forwards; --tx: 90px; --ty: -70px; }
.particles.active .particle:nth-child(10) { animation: pBurst 0.9s ease-out 0.15s forwards; --tx: -30px; --ty: -100px; }
.particles.active .particle:nth-child(11) { animation: pBurst 0.8s ease-out 0.1s forwards; --tx: 60px; --ty: -50px; }
.particles.active .particle:nth-child(12) { animation: pBurst 0.8s ease-out 0.12s forwards; --tx: -80px; --ty: -40px; }
.particles.active .particle:nth-child(13) { animation: pBurst 0.8s ease-out 0.08s forwards; --tx: 40px; --ty: -80px; }
.particles.active .particle:nth-child(14) { animation: pBurst 0.8s ease-out 0.14s forwards; --tx: -60px; --ty: -70px; }
.particles.active .particle:nth-child(15) { animation: pBurst 0.8s ease-out 0.1s forwards; --tx: 100px; --ty: -90px; }
.particles.active .particle:nth-child(16) { animation: pBurst 0.8s ease-out 0.16s forwards; --tx: -90px; --ty: -110px; }
.particles.active .particle:nth-child(17) { animation: pBurst 0.8s ease-out 0.12s forwards; --tx: 70px; --ty: -120px; }
.particles.active .particle:nth-child(18) { animation: pBurst 0.8s ease-out 0.18s forwards; --tx: -40px; --ty: -60px; }
.particles.active .particle:nth-child(19) { animation: pBurst 0.8s ease-out 0.14s forwards; --tx: 20px; --ty: -100px; }
.particles.active .particle:nth-child(20) { animation: pBurst 0.8s ease-out 0.2s forwards; --tx: -120px; --ty: -30px; }

@keyframes pBurst {
  0%   { opacity: 1; transform: translate(0, 0) scale(1); }
  100% { opacity: 0; transform: translate(var(--tx), var(--ty)) scale(0.2); }
}

/* 메시지 섹션 */
.gift-message-section {
  padding: 0 20px 16px;
  background: linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%);
}
.gift-message-bubble {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #FFF8F0;
  border: 1px solid #FDE8CD;
  padding: 12px 16px;
  border-radius: 12px;
  animation: fadeInUp 0.5s ease 0.2s both;
}
.gift-message-bubble i { font-size: 16px; color: #F59E0B; flex-shrink: 0; margin-top: 1px; }
.gift-message-bubble p { margin: 0; font-size: 14px; color: #555; line-height: 1.5; font-style: italic; }

/* ================================================================
   🎉 PHASE 2: 선물 공개
   ================================================================ */
.revealed-phase {
  position: relative;
  padding: 32px 20px;
  overflow: hidden;
  min-height: 400px;
}

.confetti-bg {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  pointer-events: none;
  overflow: hidden;
}
.confetti {
  position: absolute;
  top: -10px;
  left: var(--left);
  width: var(--size);
  height: var(--size);
  background: var(--color);
  border-radius: 2px;
  animation: confettiFall var(--duration) ease-in var(--delay) infinite;
}
.confetti:nth-child(odd) { border-radius: 50%; }

@keyframes confettiFall {
  0%   { transform: translateY(-10px) translateX(0) rotate(0deg); opacity: 1; }
  100% { transform: translateY(500px) translateX(var(--drift)) rotate(720deg); opacity: 0; }
}

.reveal-header {
  text-align: center;
  position: relative;
  z-index: 1;
  animation: fadeInDown 0.6s ease;
}
.tada {
  font-size: 48px;
  display: block;
  margin-bottom: 8px;
  animation: tadaBounce 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes tadaBounce {
  0% { transform: scale(0) rotate(-10deg); }
  60% { transform: scale(1.2) rotate(5deg); }
  100% { transform: scale(1) rotate(0deg); }
}
.reveal-header h2 { margin: 0; font-size: 22px; font-weight: 800; color: #333; }
.reveal-header p { margin: 6px 0 0; font-size: 14px; color: #999; }

/* 상품 카드 */
.product-card {
  position: relative; z-index: 1;
  margin: 24px 0;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid #f0f0f0;
}
.product-card.animate-in {
  animation: slideReveal 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both;
}
@keyframes slideReveal {
  0%   { opacity: 0; transform: translateY(60px) scale(0.9); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
.product-image-wrap {
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  background: #F8FAFC;
}
.product-image { width: 100%; height: 100%; object-fit: cover; }
.product-image-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
}
.product-image-placeholder i { font-size: 80px; color: rgba(0, 0, 0, 0.06); }
.product-detail { padding: 20px; text-align: center; }
.product-brand { display: block; font-size: 13px; color: #999; font-weight: 500; margin-bottom: 4px; }
.product-name { margin: 0; font-size: 18px; font-weight: 700; color: #333; line-height: 1.3; }

/* 메시지 */
.reveal-message {
  position: relative; z-index: 1;
  display: flex; gap: 10px; align-items: flex-start;
  background: #FFF8F0; border-left: 3px solid #F59E0B;
  padding: 14px 16px; border-radius: 8px;
  margin-bottom: 20px;
  animation: fadeInUp 0.5s ease 0.8s both;
}
.reveal-message i { font-size: 18px; color: #F59E0B; flex-shrink: 0; margin-top: 1px; }
.reveal-message p { margin: 0; font-size: 14px; color: #555; line-height: 1.5; font-style: italic; }

/* 수령 완료 */
.claim-success-box {
  position: relative; z-index: 1;
  display: flex; gap: 12px; align-items: flex-start;
  background: #F0FDF4; border: 1px solid #BBF7D0;
  border-radius: 12px; padding: 16px;
  animation: fadeInUp 0.5s ease 1s both;
}
.claim-success-box > i { font-size: 24px; color: #22C55E; flex-shrink: 0; }
.claim-success-box strong { display: block; font-size: 15px; color: #333; font-weight: 700; margin-bottom: 4px; }
.claim-success-box p { margin: 0; font-size: 13px; color: #666; }

/* ================================================================
   공통 섹션들
   ================================================================ */
.sender-info {
  display: flex; align-items: center; gap: 12px;
  padding: 16px 20px; background: #F8FAFC;
  border-bottom: 1px solid #f0f0f0;
}
.sender-avatar {
  width: 40px; height: 40px; border-radius: 50%;
  background: #2D5BFF;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.sender-avatar i { font-size: 20px; color: #fff; }
.sender-detail { flex: 1; }
.sender-label { margin: 0; font-size: 12px; color: #999; }
.sender-name { margin: 2px 0 0; font-size: 15px; font-weight: 700; color: #333; }

.receiver-hint {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 16px 20px; background: #EFF6FF;
  border-bottom: 1px solid #DBEAFE;
}
.receiver-hint > i { font-size: 22px; color: #2D5BFF; flex-shrink: 0; margin-top: 2px; }
.hint-main { margin: 0; font-size: 14px; color: #333; }
.hint-main strong { color: #2D5BFF; font-weight: 700; letter-spacing: 0.5px; }
.hint-sub { margin: 4px 0 0; font-size: 12px; color: #666; }

.verify-section { padding: 24px 20px; }
.verify-header { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 20px; }
.verify-header > i { font-size: 24px; color: #22C55E; flex-shrink: 0; margin-top: 1px; }
.verify-header h3 { margin: 0; font-size: 16px; color: #333; font-weight: 700; }
.verify-desc { margin: 2px 0 0; font-size: 13px; color: #999; }

.phone-input { margin-bottom: 12px; }
.phone-input label { display: block; font-size: 13px; font-weight: 600; color: #555; margin-bottom: 8px; }
.phone-input input {
  width: 100%; padding: 15px 16px;
  border: 1.5px solid #e0e0e0; background: #fff; color: #000;
  border-radius: 12px; font-size: 17px;
  text-align: center; letter-spacing: 2px; font-weight: 600;
  transition: all 0.2s; box-sizing: border-box;
}
.phone-input input::placeholder { color: #bbb; font-weight: 400; }
.phone-input input:focus {
  outline: none; border-color: #2D5BFF;
  background: #F8FAFF;
  box-shadow: 0 0 0 3px rgba(45, 91, 255, 0.1);
}

.claim-btn {
  width: 100%; margin-top: 4px; padding: 16px;
  border: none; border-radius: 12px;
  background: #2D5BFF; color: #fff;
  font-size: 16px; font-weight: 700;
  cursor: pointer; transition: all 0.2s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.claim-btn i { font-size: 18px; }
.claim-btn:hover:not(:disabled) { background: #1E4AE6; }
.claim-btn:active:not(:disabled) { transform: scale(0.98); }
.claim-btn:disabled { background: #ccc; cursor: not-allowed; }

.safety-note {
  display: flex; align-items: center; gap: 6px;
  justify-content: center; margin-top: 14px;
}
.safety-note i { font-size: 13px; color: #999; flex-shrink: 0; }
.safety-note span { font-size: 11px; color: #999; line-height: 1.4; }

/* 의심되시나요 */
.trust-section { border-top: 1px solid #f0f0f0; padding: 0 20px; }
.trust-toggle {
  width: 100%; display: flex; align-items: center; gap: 8px;
  padding: 16px 0; background: none; border: none;
  cursor: pointer; font-size: 13px; font-weight: 600; color: #666;
}
.trust-toggle > i:first-child { font-size: 18px; color: #999; }
.trust-toggle > span { flex: 1; text-align: left; }
.trust-toggle > i:last-child { font-size: 14px; color: #999; }

.trust-content { padding: 0 0 20px; display: flex; flex-direction: column; gap: 14px; }
.trust-item { display: flex; gap: 10px; align-items: flex-start; }
.trust-item > i { font-size: 18px; color: #22C55E; flex-shrink: 0; margin-top: 1px; }
.trust-item strong { display: block; font-size: 13px; color: #333; font-weight: 700; margin-bottom: 2px; }
.trust-item p { margin: 0; font-size: 12px; color: #666; line-height: 1.5; }
.trust-item a { color: #2D5BFF; text-decoration: none; font-weight: 600; }

.slide-enter-active, .slide-leave-active { transition: all 0.3s ease; overflow: hidden; }
.slide-enter-from, .slide-leave-to { opacity: 0; max-height: 0; padding-bottom: 0; }
.slide-enter-to, .slide-leave-from { opacity: 1; max-height: 500px; }

/* 푸터 */
.footer-info {
  text-align: center; padding: 16px 20px;
  border-top: 1px solid #f0f0f0; background: #FAFAFA;
}
.footer-company { margin: 0; font-size: 12px; color: #999; font-weight: 600; }
.footer-contact { margin: 4px 0 0; font-size: 12px; color: #bbb; }
.footer-contact a { color: #2D5BFF; text-decoration: none; font-weight: 600; }

/* 모바일 */
@media (max-width: 480px) {
  .gift-claim-page { padding: 0; background: #fff; }
  .container { min-height: 100vh; border-radius: 0; box-shadow: none; }
}

/* 토스트 */
.toast {
  position: fixed; bottom: 32px; left: 50%;
  transform: translateX(-50%);
  background: #333; color: #fff;
  padding: 14px 24px; border-radius: 50px;
  display: flex; align-items: center; gap: 10px;
  font-weight: 700; font-size: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  z-index: 1100; max-width: calc(100% - 40px);
}
.toast-icon { font-size: 18px; }
.toast.success .toast-icon { color: #22C55E; }
.toast.error .toast-icon { color: #EF4444; }
.toast-enter-active, .toast-leave-active { transition: all 0.3s ease; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(20px); }

/* 유틸 */
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fadeInDown {
  from { opacity: 0; transform: translateY(-16px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>